/**
 * gRPC client interceptor: acquires an S2S token from the configured
 * `S2STokenManager` and attaches it to the outgoing `authorization`
 * metadata header as `Bearer <token>`.
 *
 * Lifted 1:1 from provisioning's
 * `server/grpc/interceptors/auth/client/auth-client-interceptor.ts`.
 */
import {
  type Interceptor,
  type InterceptorOptions,
  InterceptingCall,
  Metadata,
} from "@grpc/grpc-js";
import { X_USER_CONTEXT_HEADER } from "../types/user-actor";
import { userContextStore } from "../user-context-als";
import type { S2STokenManager } from "./s2s-token-manager";

export type ClientAuthOptions = {
  tokenManager: S2STokenManager;
  audience: string;
  scopes?: string[];
  instanceId?: string;
};

/**
 * Creates a gRPC client interceptor that acquires an S2S token and adds
 * it to the metadata.
 *
 * Usage:
 *
 * ```ts
 * const client = new MyGrpcServiceClient(target, credentials, {
 *   interceptors: [
 *     createClientAuthInterceptor({
 *       tokenManager,
 *       audience: "nucleus-internal",
 *       scopes: ["flows:write"],
 *       instanceId: process.env.INSTANCE_ID,
 *     }),
 *   ],
 * });
 * ```
 */
export function createClientAuthInterceptor(
  opts: ClientAuthOptions,
): Interceptor {
  return (options: InterceptorOptions, nextCall) => {
    return new InterceptingCall(nextCall(options), {
      async start(metadata, listener, next) {
        try {
          const token = await opts.tokenManager.getToken({
            audience: opts.audience,
            scopes: opts.scopes,
            instanceId: opts.instanceId,
          });

          metadata.set("authorization", `Bearer ${token}`);

          // A2 / D454 — FORWARD ON SEND. Relay the RAW inbound `x-user-context`
          // captured by the server auth path (ambient store) onto this outgoing
          // S2S call, so user identity propagates across A→B hops. The raw bytes
          // are forwarded UNCHANGED (never re-encoded) — preserving byte-parity
          // + the D454 `on_behalf_of` agent marker. Absent store (S2S-only call
          // originated by a worker, not an inbound user request) → nothing added.
          const rawUserContext = userContextStore.getStore();
          if (rawUserContext) {
            metadata.set(X_USER_CONTEXT_HEADER, rawUserContext);
          }
        } catch (_err) {
          listener.onReceiveStatus?.({
            code: 16, // UNAUTHENTICATED
            details: "Failed to acquire S2S token",
            metadata: new Metadata(),
          });
          return;
        }

        next(metadata, listener);
      },
    });
  };
}
